import { COUNTRY_LANGUAGES, DEFAULT_COUNTRY } from '@src/config/localisation/locale-config.constants'
import { LOCALE_COOKIE } from '@src/config/localisation/locale-config.static-constants'
import type { TAbsoluteLocale } from '@src/config/localisation/locale-config.types'
import { getLocaleConfig } from '@src/config/localisation/statsig-locale-config-constants'
import getCookie from '@src/quince-site-common/src/utils/getCookie'
import { TelemetryIntegration } from '@src/telemetry/telemetry-events.types'
import { getLocalisationDataFromStorage } from '@src/utils/general.utils'
import { parseLocale } from '@src/utils/i18n.utils'

import { initializeScript } from '../provider-utils/initialize-script'

export let isTrustARCScriptLoading = true
export let resolveTrustArcInitialisePromise: (value: unknown) => void
export const initialiseTrustARCPromise = new Promise(resolve => {
  resolveTrustArcInitialisePromise = resolve // Store the resolver for later use
})

const TRUSTARC_READY_EVENT = 'trustarc-ccm-ready'
const TRUSTARC_CLOSE_TOPIC = 'close'
const TRUSTARC_READY_LISTENER_KEY = '__quinceTrustArcReadyListenerAttached'
const TRUSTARC_CLOSE_LISTENER_KEY = '__quinceTrustArcCloseListenerAttached'

const attachTrustArcCloseListener = () => {
  const trustArcApi = window.truste?.eu as (typeof window.truste.eu & Record<string, boolean>) | undefined
  if (!trustArcApi?.addEventListener) return
  if (trustArcApi[TRUSTARC_CLOSE_LISTENER_KEY]) return

  trustArcApi.addEventListener(topic => {
    if (topic === TRUSTARC_CLOSE_TOPIC) {
      window.location.reload()
    }
  })

  trustArcApi[TRUSTARC_CLOSE_LISTENER_KEY] = true
}

const addTrustArcReadyListenerToBody = () => {
  const body = document.body as (typeof document.body & Record<string, boolean>) | null
  if (!body) return false
  if (body[TRUSTARC_READY_LISTENER_KEY]) return false

  body.addEventListener(TRUSTARC_READY_EVENT, attachTrustArcCloseListener)
  body[TRUSTARC_READY_LISTENER_KEY] = true
  return true
}

export const attachTrustArcCloseReloadListener = () => {
  if (typeof document === 'undefined') return

  if (addTrustArcReadyListenerToBody()) return

  if (typeof MutationObserver === 'undefined') return
  if (!document.documentElement) return
  if (typeof Node !== 'undefined' && !(document.documentElement instanceof Node)) return

  const observer = new MutationObserver(() => {
    if (addTrustArcReadyListenerToBody()) {
      observer.disconnect()
    }
  })

  observer.observe(document.documentElement, { childList: true })
}

export const getTrustArcLanguageCode = (): string => {
  try {
    const locale = getCookie(LOCALE_COOKIE)
    if (locale) {
      const { language, country } = parseLocale(locale as TAbsoluteLocale)
      return `${language}_${country?.toLowerCase()}`
    }

    return COUNTRY_LANGUAGES[DEFAULT_COUNTRY][0]
  } catch (error) {
    console.error('Error getting TrustArc language code:', error)
    return COUNTRY_LANGUAGES[DEFAULT_COUNTRY][0]
  }
}

// @i18n-ignore
const DEFAULT_TRUSTARC_DOMAIN = 'quince.com'
// @i18n-ignore
const DEFAULT_TRUSTARC_COOKIE_NAME = 'quince_us'

export const getTrustArcDomain = (): string => {
  try {
    const locale = getCookie(LOCALE_COOKIE)
    const localeConfig = getLocaleConfig(locale ?? '')
    return localeConfig.trustArcDomain ?? DEFAULT_TRUSTARC_DOMAIN
  } catch (error) {
    console.error('Error getting TrustArc domain:', error)
    return DEFAULT_TRUSTARC_DOMAIN
  }
}

export const getTrustArcCookieName = (): string => {
  try {
    const localisationData = getLocalisationDataFromStorage()
    const locale = localisationData?.locale
    const localeConfig = getLocaleConfig(locale ?? '')
    return localeConfig.trustArcCookieName ?? DEFAULT_TRUSTARC_COOKIE_NAME
  } catch (error) {
    console.error('Error getting TrustArc cookie name:', error)
    return DEFAULT_TRUSTARC_COOKIE_NAME
  }
}

export const TRUSTARC_CONFIG: TelemetryIntegration = {
  config: {
    enabled: true,
    eventIdealTimeout: 1000,
  },
  initialize: async () => {
    const domain = getTrustArcDomain()
    // Close-reload listener and extra notice params are only applied for non-US/CA locales
    // (i.e. domains other than quince.com). Applying them unconditionally on US/CA risks
    // invalidating existing consent records and causing unexpected banner re-prompts.
    const isNonDefaultDomain = domain !== DEFAULT_TRUSTARC_DOMAIN

    if (isNonDefaultDomain) {
      attachTrustArcCloseReloadListener()
    }

    await initializeScript({
      key: 'trustarc',
      domain: 'https://consent.trustarc.com',
      path: () => {
        const cookieName = getTrustArcCookieName()
        const languageCode = getTrustArcLanguageCode()
        if (isNonDefaultDomain) {
          return `/notice?domain=${domain}&c=teconsent&js=nj&noticeType=bb&text=true&gtm=1&cookieName=${cookieName}&language=${languageCode}`
        }
        // Original URL format preserved for US/CA (quince.com domain)
        return `/notice?domain=${domain}&c=teconsent&js=nj&noticeType=bb&gtm=1&language=${languageCode}`
      },
      injectAt: 'beforeFirstScript',
      onLoad: () => {
        resolveTrustArcInitialisePromise(null)
        isTrustARCScriptLoading = false
      },
    })
  },
}
